// tracking form dirty state from formik and other sources as well
// helps prevent data loss on navigation or browser close
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useBlocker } from "react-router-dom";
import PropTypes from "prop-types";
import { GlobalFormWarningDialog } from "../../components/admin/elements/global-form-warning-dialog/global-form-warning-dialog";

const FormDirtyContext = createContext(undefined);

export const FormDirtyProvider = ({ children }) => {
  const [dirtySources, setDirtySources] = useState(new Map());
  const isProceedingRef = useRef(false);

  const [dialogState, setDialogState] = useState({
    open: false,
    path: null,
    title: null,
    message: null,
    blocker: null,
    onProceed: null,
  });
  const navigate = useNavigate();

  const isDirty = Array.from(dirtySources.values()).includes(true);

  // Block navigation when form is dirty (but not when proceeding after save -- using isProceedingRef)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !isProceedingRef.current &&
      currentLocation.pathname !== nextLocation.pathname
  );

  // Handle blocked navigation
  useEffect(() => {
    if (blocker.state === "blocked") {
      setDialogState({
        open: true,
        path: blocker.location.pathname,
        title: null,
        message: null,
        blocker: blocker,
        onProceed: null,
      });
    }
  }, [blocker]);

  const setFormDirty = useCallback((dirty, sourceId = "default") => {
    setDirtySources((prev) => {
      if (dirty) {
        if (prev.get(sourceId) === true) return prev;
        const newMap = new Map(prev);
        newMap.set(sourceId, true);
        return newMap;
      } else {
        if (!prev.has(sourceId)) return prev;
        const newMap = new Map(prev);
        newMap.delete(sourceId);
        return newMap;
      }
    });
  }, []);

  const clearSource = useCallback((sourceId) => {
    setDirtySources((prev) => {
      const newMap = new Map(prev);
      newMap.delete(sourceId);
      return newMap;
    });
  }, []);

  const clearForm = useCallback(() => {
    isProceedingRef.current = true;
    setDirtySources(new Map());
    // Reset the flag after a short delay
    setTimeout(() => {
      isProceedingRef.current = false;
    }, 100);
  }, []);

  // Imperative guard for navigations that DON'T go through react-router, so
  // useBlocker can't see them. The asset editor switches the edited entity by
  // mutating a Zustand store + history.replaceState (no router navigation), so
  // those call sites must ask permission here instead. When clean (or already
  // proceeding after a confirmed discard) the action runs immediately; when
  // dirty it surfaces the same discard dialog and defers `proceed` until the
  // user confirms.
  const requestNavigation = useCallback(
    (proceed) => {
      if (!isDirty || isProceedingRef.current) {
        proceed();
        return;
      }
      setDialogState({
        open: true,
        path: null,
        title: null,
        message: null,
        blocker: null,
        onProceed: proceed,
      });
    },
    [isDirty]
  );

  const handleDialogClose = useCallback(() => {
    if (dialogState.blocker) {
      dialogState.blocker.reset();
    }
    setDialogState({
      open: false,
      path: null,
      title: null,
      message: null,
      blocker: null,
      onProceed: null,
    });
  }, [dialogState.blocker]);

  const handleDialogConfirm = useCallback(() => {
    const { blocker: pendingBlocker, path, onProceed } = dialogState;
    clearForm();

    if (pendingBlocker) {
      pendingBlocker.proceed();
    } else if (onProceed) {
      // Non-router navigation (e.g. asset-tree switch): run the deferred action
      // now that the user has chosen to discard.
      onProceed();
    } else if (path) {
      navigate(path);
    }

    setDialogState({
      open: false,
      path: null,
      title: null,
      message: null,
      blocker: null,
      onProceed: null,
    });
  }, [dialogState, navigate, clearForm]);

  // Prevent browser navigation when form is dirty
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const value = useMemo(() => ({
    isDirty,
    setFormDirty,
    clearSource,
    clearForm,
    requestNavigation,
  }), [isDirty, setFormDirty, clearSource, clearForm, requestNavigation]);

  return (
    <FormDirtyContext.Provider
      value={value}
    >
      {children}

      <GlobalFormWarningDialog
        open={dialogState.open}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        title={dialogState.title}
        message={dialogState.message}
      />
    </FormDirtyContext.Provider>
  );
};

FormDirtyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useFormDirty = () => {
  const context = useContext(FormDirtyContext);
  if (context === undefined) {
    throw new Error("useFormDirty must be used within a FormDirtyProvider");
  }
  return context;
};
